import { t } from "@lingui/macro";
import { CommissionUnit } from "@src/__generated__/urql-graphql";
import { minValue } from "@src/utils/forms/validators";
import type { NumberModel } from "@src/utils/models";
import { FieldState } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";
import { BudgetForm } from "./BudgetForm";

export class ExpenseBudgetForm extends BudgetForm {
  expense_budget_category_id: string;
  real_expenses: number;
  @observable.ref completed_at?: string | null;

  planned_commission: FieldState<NumberModel>;
  commission_unit: FieldState<CommissionUnit>;
  planned_expenses: FieldState<NumberModel>;

  constructor(
    args: ConstructorParameters<typeof BudgetForm>[0] & {
      expense_budget_category_id: string;
      real_expenses?: number | null;
      completed_at?: string | null;
      planned_commission: NumberModel;
      commission_unit: CommissionUnit;
      planned_expenses: NumberModel;
    },
  ) {
    super(args);

    makeObservable(this);

    this.gtePlannedExpenses = this.gtePlannedExpenses.bind(this);
    this.recalculateTotalBudget = this.recalculateTotalBudget.bind(this);
    this.recalculatePlannedCommission =
      this.recalculatePlannedCommission.bind(this);

    this.total_budget
      .onDidChange(this.recalculatePlannedCommission)
      .validators(this.gtePlannedExpenses);

    this.expense_budget_category_id = args.expense_budget_category_id;
    this.real_expenses = args.real_expenses ?? 0;
    this.completed_at = args.completed_at;

    this.commission_unit = new FieldState(args.commission_unit).onDidChange(
      this.recalculateTotalBudget,
    );
    this.planned_commission = new FieldState(args.planned_commission)
      .onDidChange(this.recalculateTotalBudget)
      .validators(minValue(0));
    this.planned_expenses = new FieldState(args.planned_expenses)
      .onDidChange(this.recalculateTotalBudget)
      .validators(minValue(0));

    this._form.$.push(this.planned_commission, this.commission_unit);
  }

  @computed get isCompleted() {
    return !!this.completed_at;
  }

  @action.bound
  setCompletedAt(val: ExpenseBudgetForm["completed_at"]) {
    this.completed_at = val;
  }

  serialize() {
    return {
      commission: this.planned_commission.value.denominatedValue,
      commission_unit: this.commission_unit.value,
      expense_budget_category_id: this.expense_budget_category_id,
      ...super.serialize(),
    };
  }

  /**
   * The given field must be greater than or equal planned_expenses.
   */
  private gtePlannedExpenses($: NumberModel) {
    if ($.denominatedValue < this.planned_expenses.value.denominatedValue) {
      return t`Budget can't be less then expenses`;
    }

    return null;
  }

  private recalculatePlannedCommission(args: {
    newValue: NumberModel;
    oldValue: NumberModel;
  }) {
    if (this.commission_unit.value !== CommissionUnit.Absolute) {
      this.commission_unit.onChange(CommissionUnit.Absolute);
    }

    const value =
      args.newValue.denominatedValue -
      this.planned_expenses.value.denominatedValue;

    this.planned_commission.reset(
      this.planned_commission.value.setDenomValue(value),
    );
  }

  private recalculateTotalBudget() {
    let value: number | undefined;

    if (this.commission_unit.value === CommissionUnit.Absolute) {
      value =
        this.planned_expenses.value.denominatedValue +
        this.planned_commission.value.denominatedValue;
    } else if (this.commission_unit.value === CommissionUnit.Relative) {
      value =
        this.planned_expenses.value.denominatedValue +
        Math.trunc(
          (this.planned_expenses.value.denominatedValue *
            this.planned_commission.value.value) /
            100,
        );
    }

    if (value !== undefined) {
      this.total_budget.reset(this.total_budget.value.setDenomValue(value));
    }
  }
}
